// src/pages/GovInboxTab.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "../api/axios";
import "./GovInboxTab.css";

import UserProfileModal from "../components/users/UserProfileModal";
import AssignStaffPopover from "../components/assign/AssignStaffPopover";
import ApplicationFullViewModal from "../components/gos/ApplicationFullViewModal";
import ResponsibleAvatars from "../components/responsibles/ResponsibleAvatars";

/* ─── pure helpers ──────────────────────────────────────────── */
const pick = (obj, key, fallback = "") => (obj && obj[key] != null ? obj[key] : fallback);

const normalizePayload = (res) => {
  const p = res?.data ?? {};

  if (Array.isArray(p)) {
    return { items: p, meta: null };
  }

  return {
    items: Array.isArray(p.data) ? p.data : [],
    meta: p.meta || null,
  };
};

const norm = (v) => String(v || "").trim().toLowerCase();

const dateOnly = (v) => (v ? String(v).slice(0, 10) : "—");

const localizedName = (v, locale = "kz", fallback = "—") => {
  if (v == null || v === "") return fallback;
  if (typeof v === "string" || typeof v === "number") return String(v);

  const lang = locale === "kz" ? "kz" : "ru";

  return (
    v[`name_${lang}`] ||
    v.name ||
    v.title ||
    v.label ||
    fallback
  );
};

const getRegistrationNumber = (g) =>
  g?.registration_number ||
  g?.group_registration_number ||
  g?.application_group_registration_number ||
  g?.group?.registration_number ||
  g?.application_group?.registration_number ||
  g?.applications?.find?.((a) => a?.registration_number)?.registration_number ||
  g?.applications?.find?.((a) => a?.group?.registration_number)?.group?.registration_number ||
  "";

const getSentAt = (v) =>
  v?.sent_at ||
  v?.group_sent_at ||
  v?.submitted_at ||
  v?.group?.sent_at ||
  v?.application_group?.sent_at ||
  null;

const getResponsibles = (g) =>
  (Array.isArray(g?.responsibles) && g.responsibles) ||
  (Array.isArray(g?.assignees) && g.assignees) ||
  [];

const respId = (u) => u?.user_id ?? u?.id ?? null;

const getDecisionRaw = (decision) => decision?.status ?? decision;

/* ─── component ─────────────────────────────────────────────── */
export default function GovInboxTab({ locale = "kz", isHead = false }) {
  /* ── i18n ─────────────────────────────────────────────────── */
  const t = useMemo(() => {
    const kz = {
      title: "Жаңа өтінімдер",
      refresh: "Жаңарту",
      empty: "Тізім бос",
      err: "Қате",
      searchPh: "Іздеу (топ атауы, баспа немесе тіркеу нөмірі)...",
      onlyNotAssigned: "Тек тағайындалмаған",
      sortLabel: "Сұрыптау",
      perPage: "Саны",
      reset: "Таза",
      needsDecision: "Шешім қажет",

      sortSentDesc: "Жаңадан жіберілгендер",
      sortSentAsc: "Бұрын жіберілгендер",
      sortTitleAsc: "Атауы: A → Я",
      sortTitleDesc: "Атауы: Я → A",

      page: "Бет",
      of: "із",
      prev: "Алдыңғы",
      next: "Келесі",

      publisher: "Баспасы",
      status: "Күйі",
      assignees: "Тағайындалғандар",
      notAssigned: "Тағайындалмаған",
      assign: "Тағайындау",
      modalTitle: "Қызметкерлерді тағайындау",
      cancel: "Болдырмау",
      save: "Сақтау",
      loading: "Жүктелуде...",
      staffEmpty: "Қызметкерлер табылмады",

      groupInfoTitle: "Топ туралы жалпы ақпарат",
      registration_number: "Тіркеу нөмірі",
      noRegistrationNumber: "Тіркеу нөмірі жоқ",
      subject: "Пән",
      grade: "Сынып",
      education_level: "Білім деңгейі",
      direction: "Бағыт",
      development_language: "Әзірлеу тілі",
      teaching_language: "Оқыту тілі",
      publication_type: "Басылым түрі",
      service_type: "Қызмет түрі",
      submission_year: "Өткізу жылы",
      sent_at: "Жіберілген күні",

      apps: "Өтінімдер",
      appCategory: "Санат",
      appStatus: "Күйі",
      appDecision: "Шешім",
      appDate: "Жіберілген күні",

      open: "Ашу",
      close: "Жабу",
      viewFull: "Толық көру",
      appModalTitle: "Өтінім туралы толық ақпарат",
      profileTitle: "Қолданушы профилі",
      closeProfile: "Жабу",
    };

    const ru = {
      title: "Новые заявки",
      refresh: "Обновить",
      empty: "Список пуст",
      err: "Ошибка",
      searchPh: "Поиск (название группы, издательство или рег. номер)...",
      onlyNotAssigned: "Только не назначено",
      sortLabel: "Сортировка",
      perPage: "Количество",
      reset: "Сброс",
      needsDecision: "Требует решения",

      sortSentDesc: "Сначала отправленные новые",
      sortSentAsc: "Сначала отправленные ранние",
      sortTitleAsc: "Название: A → Я",
      sortTitleDesc: "Название: Я → A",

      page: "Стр.",
      of: "из",
      prev: "Назад",
      next: "Вперёд",

      publisher: "Издательство",
      status: "Статус",
      assignees: "Назначено",
      notAssigned: "Не назначено",
      assign: "Назначить",
      modalTitle: "Назначить сотрудников",
      cancel: "Отмена",
      save: "Сохранить",
      loading: "Загрузка...",
      staffEmpty: "Сотрудники не найдены",

      groupInfoTitle: "Общая информация группы",
      registration_number: "Регистрационный номер",
      noRegistrationNumber: "Регистрационный номер отсутствует",
      subject: "Предмет",
      grade: "Класс",
      education_level: "Уровень образования",
      direction: "Направление",
      development_language: "Язык разработки",
      teaching_language: "Язык обучения",
      publication_type: "Тип издания",
      service_type: "Тип услуги",
      submission_year: "Год подачи",
      sent_at: "Дата отправки",

      apps: "Заявки",
      appCategory: "Категория",
      appStatus: "Статус",
      appDecision: "Решение",
      appDate: "Дата отправки",

      open: "Развернуть",
      close: "Свернуть",
      viewFull: "Подробнее",
      appModalTitle: "Полная информация по заявке",
      profileTitle: "Профиль пользователя",
      closeProfile: "Закрыть",
    };

    return locale === "kz" ? kz : ru;
  }, [locale]);

  const langHeader = useMemo(() => (locale === "kz" ? "kk" : locale), [locale]);

  const headers = useMemo(() => ({
    Authorization: `Bearer ${localStorage.getItem("token")}`,
    "Accept-Language": langHeader,
  }), [langHeader]);

  const staffParams = useMemo(() => ({
    per_page: 200,
    status: "белсенді",
    roles: "staff",
  }), []);

  /* ── state ────────────────────────────────────────────────── */
  const [rawGroups, setRawGroups] = useState([]);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [profileOpen, setProfileOpen] = useState(false);
  const [profileUser, setProfileUser] = useState(null);
  const [profileDetails, setProfileDetails] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileReviewsLoading, setProfileReviewsLoading] = useState(false);
  const [profileReviewsResp, setProfileReviewsResp] = useState(null);
  const [profileMyReview, setProfileMyReview] = useState(null);

  const LS_KEY = "gos_expanded_groups";

  const [expandedIds, setExpandedIds] = useState(() => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      const arr = raw ? JSON.parse(raw) : [];
      return new Set((Array.isArray(arr) ? arr : []).map(String));
    } catch {
      return new Set();
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(LS_KEY, JSON.stringify(Array.from(expandedIds)));
    } catch {}
  }, [expandedIds]);

  const toggleExpand = useCallback((id) => {
    const sid = String(id);

    setExpandedIds((prev) => {
      const next = new Set(prev);
      next.has(sid) ? next.delete(sid) : next.add(sid);
      return next;
    });
  }, []);

  /* ── filter / sort state ──────────────────────────────────── */
  const [search, setSearch] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");
  const [onlyNotAssigned, setOnlyNotAssigned] = useState(false);
  const [sortBy, setSortBy] = useState("sent_at");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  useEffect(() => {
    const id = setTimeout(() => setSearchDebounced(search.trim()), 300);
    return () => clearTimeout(id);
  }, [search]);

  const resetFilters = useCallback(() => {
    setSearch("");
    setSearchDebounced("");
    setOnlyNotAssigned(false);
    setSortBy("sent_at");
    setSortDir("desc");
    setPerPage(10);
    setPage(1);
  }, []);

  const mountedRef = useRef(true);

  /* ── data loading ─────────────────────────────────────────── */
  const loadGroups = useCallback(async (opts = {}) => {
    const { soft = false } = opts;

    if (soft) setRefreshing(true);
    else setLoading(true);

    setErr("");

    try {
      const params = {
        page: 1,
        per_page: 100,
        sort_by: "sent_at",
        sort_dir: "desc",
        view_as: "head",
        _ts: Date.now(),
      };

      const res = await axios.get("/backoffice/applications/grouped", {
        headers,
        params,
      });

      const { items } = normalizePayload(res);

      if (mountedRef.current) {
        setRawGroups(items || []);
      }
    } catch (e) {
      if (mountedRef.current) {
        setRawGroups([]);
        setErr(e?.response?.data?.message || e?.message || t.err);
      }
    } finally {
      if (soft) setRefreshing(false);
      else setLoading(false);
    }
  }, [headers, t.err]);

  useEffect(() => {
    mountedRef.current = true;
    loadGroups({ soft: false });

    return () => {
      mountedRef.current = false;
    };
  }, [loadGroups]);

  useEffect(() => {
    if (document.getElementById("badge-pulse-keyframes")) return;

    const style = document.createElement("style");
    style.id = "badge-pulse-keyframes";
    style.textContent =
      "@keyframes badge-pulse{0%,100%{opacity:1;transform:scale(1)}50%{opacity:.35;transform:scale(1.06)}}";

    document.head.appendChild(style);
  }, []);

  /* ── status / decision helpers ────────────────────────────── */
  const STATUS_DICT = useMemo(() => ({
    submitted: { ru: "Отправлено", kz: "Жіберілді" },
    sent: { ru: "Отправлено", kz: "Жіберілді" },
    draft: { ru: "Черновик", kz: "Жоба" },
    new: { ru: "Новая", kz: "Жаңа" },
    accepted: { ru: "Принято", kz: "Қабылданды" },
    approved: { ru: "Одобрено", kz: "Мақұлданды" },
    declined: { ru: "Отклонено", kz: "Қабылданбады" },
    rejected: { ru: "Отклонено", kz: "Қабылданбады" },
    in_review: { ru: "На рассмотрении", kz: "Қаралуда" },
    reviewing: { ru: "На рассмотрении", kz: "Қаралуда" },

    returned: { ru: "Возвращено на доработку", kz: "Өңдеуге қайтарылды" },
    back: { ru: "После доработки", kz: "Өңдеуден келген" },
    ondeu: { ru: "После доработки", kz: "Өңдеуден келген" },
  }), []);

  const statusNormKey = useCallback((raw) => {
    const s = norm(raw);

    if (!s || s === "-") return null;

    if (s === "back" || s === "ondeu" || s === "after_revision" || s === "from_revision" || s === "resubmitted") {
      return "ondeu";
    }

    if (s === "returned" || s === "return" || s === "returned_for_revision") {
      return "returned";
    }

    if (["submitted", "sent", "отправ", "жібер"].some((x) => s.includes(x))) {
      return "submitted";
    }

    if (["declined", "rejected", "отклон", "қабылданба"].some((x) => s.includes(x))) {
      return "declined";
    }

    if (["accepted", "принят", "қабылдан"].some((x) => s.includes(x))) {
      return "accepted";
    }

    if (["approved", "одобрен", "мақұл"].some((x) => s.includes(x))) {
      return "approved";
    }

    if (["draft", "чернов", "жоба"].some((x) => s.includes(x))) {
      return "draft";
    }

    if (["in_review", "review", "рассмотр", "қарал"].some((x) => s.includes(x))) {
      return "in_review";
    }

    // Өңдеуден келген — повторно пришло после доработки.
    if (
      s.includes("өңдеуден келген") ||
      s.includes("после доработ") ||
      s.includes("из доработ")
    ) {
      return "ondeu";
    }

    // Өңдеуге қайтарылды — решение "return".
    if (
      s.includes("өңдеуге қайтар") ||
      s.includes("возвращено на доработ") ||
      s.includes("возвращен на доработ") ||
      s.includes("қайтар")
    ) {
      return "returned";
    }

    if (["new", "нов", "жаңа"].some((x) => s.includes(x))) {
      return "new";
    }

    return null;
  }, []);

  const decisionNormKey = useCallback((decision) => {
    const raw = getDecisionRaw(decision);

    if (raw == null) return null;

    const d = String(raw).trim().toLowerCase();

    if (
      !d ||
      d === "null" ||
      d === "undefined" ||
      d === "none" ||
      d === "—" ||
      d === "-"
    ) {
      return null;
    }

    if (
      d === "reject_expertise" ||
      d === "reject" ||
      d.includes("сараптамадан бас тарт") ||
      d.includes("отказано") ||
      d.includes("отказ")
    ) {
      return "reject_expertise";
    }

    if (
      d === "return" ||
      d.includes("өңдеуге қайтар") ||
      d.includes("қайтар") ||
      d.includes("возвращ")
    ) {
      return "return";
    }

    if (
      d === "accept" ||
      d.includes("қабылдан") ||
      d.includes("принят")
    ) {
      return "accept";
    }

    return d;
  }, []);

  const isAppNeedsDecision = useCallback((app) => {
    const statusKey = statusNormKey(app?.status);
    const decisionKey = decisionNormKey(app?.decision);

    // Жаңа өтінім:
    // шешім жоқ болса ғана +1.
    if (statusKey === "new") {
      return decisionKey == null;
    }

    // Өңдеуге қайтарылды:
    // return шешімі бар болса, +1 емес.
    // decision бос болса ғана +1.
    if (statusKey === "returned") {
      return decisionKey == null;
    }

    // Өңдеуден келген:
    // шешім жоқ болса => +1.
    // шешім әлі return болса => бұл ескі шешім, +1.
    // accept / reject_expertise болса => 0.
    if (statusKey === "ondeu") {
      return decisionKey == null || decisionKey === "return";
    }

    return false;
  }, [statusNormKey, decisionNormKey]);

  const statusLabel = useCallback((raw) => {
    const key = statusNormKey(raw);

    if (!key) return String(raw || "-");

    const row = STATUS_DICT[key];

    return locale === "kz"
      ? row?.kz || String(raw || "-")
      : row?.ru || String(raw || "-");
  }, [STATUS_DICT, locale, statusNormKey]);

  const statusColorKey = useCallback((raw) => {
    const key = statusNormKey(raw);

    if (!key) return "neutral";
    if (key === "accepted" || key === "approved") return "ok";
    if (key === "submitted" || key === "sent") return "sent";
    if (["draft", "new", "in_review", "reviewing", "returned", "back", "ondeu"].includes(key)) return "warn";
    if (key === "declined" || key === "rejected") return "danger";

    return "neutral";
  }, [statusNormKey]);

  const statusBadgeClass = useCallback((raw) => {
    const k = statusColorKey(raw);

    if (k === "ok") return "badge badge--ok";
    if (k === "sent") return "badge badge--sent";
    if (k === "warn") return "badge badge--warn";
    if (k === "danger") return "badge badge--danger";

    return "badge badge--neutral";
  }, [statusColorKey]);

  const statusBadgeStyle = useCallback((app) =>
    isAppNeedsDecision(app)
      ? { animation: "badge-pulse 1.2s ease-in-out infinite" }
      : {},
  [isAppNeedsDecision]);

  const groupHeaderClass = useCallback((raw) => {
    const k = statusColorKey(raw);

    if (k === "sent") return "group-status status-submitted";
    if (k === "ok") return "group-status status-accepted";
    if (k === "danger") return "group-status status-external";
    if (k === "warn") return "group-status status-processing";

    return "group-status status-unsent";
  }, [statusColorKey]);

  const formatDecision = useCallback((decision) => {
    const key = decisionNormKey(decision);

    if (!key) return "—";

    if (key === "accept") {
      return locale === "kz" ? "Қабылданды" : "Принято";
    }

    if (key === "return") {
      return locale === "kz" ? "Өңдеуге қайтарылды" : "Возвращено на доработку";
    }

    if (key === "reject_expertise") {
      return locale === "kz" ? "Сараптамадан бас тартылды" : "Отказано в экспертизе";
    }

    return String(getDecisionRaw(decision));
  }, [locale, decisionNormKey]);

  /* ── filtered + sorted + paginated data ───────────────────── */
  const filtered = useMemo(() => {
    const q = norm(searchDebounced);
    let list = Array.isArray(rawGroups) ? [...rawGroups] : [];

    if (q) {
      list = list.filter((g) =>
        norm(g?.title).includes(q) ||
        norm(g?.publisher_name).includes(q) ||
        norm(g?.publisher?.name).includes(q) ||
        norm(g?.publisher?.organization_name).includes(q) ||
        norm(g?.publisher?.full_name).includes(q) ||
        norm(getRegistrationNumber(g)).includes(q)
      );
    }

    if (onlyNotAssigned) {
      list = list.filter((g) => getResponsibles(g).length === 0);
    }

    const getSortDate = (g) => String(getSentAt(g) || "");

    const cmpStr = (a, b) =>
      String(a || "").localeCompare(String(b || ""), "ru", { sensitivity: "base" });

    list.sort((a, b) => {
      const r = sortBy === "title"
        ? cmpStr(a?.title, b?.title)
        : cmpStr(getSortDate(a), getSortDate(b));

      return sortDir === "asc" ? r : -r;
    });

    return list;
  }, [rawGroups, searchDebounced, onlyNotAssigned, sortBy, sortDir]);

  const total = filtered.length;
  const lastPage = Math.max(1, Math.ceil(total / Math.max(1, perPage)));
  const pageSafe = Math.min(Math.max(1, page), lastPage);

  useEffect(() => {
    if (page !== pageSafe) {
      setPage(pageSafe);
    }
  }, [page, pageSafe]);

  const paged = useMemo(() => {
    const start = (pageSafe - 1) * perPage;
    return filtered.slice(start, start + perPage);
  }, [filtered, pageSafe, perPage]);

  const canPrev = pageSafe > 1;
  const canNext = pageSafe < lastPage;

  const gotoPage = useCallback((p) => {
    setPage(Math.min(Math.max(1, p), lastPage));
  }, [lastPage]);

  const getPageButtons = useCallback(() => {
    const pages = [];

    if (lastPage <= 7) {
      for (let i = 1; i <= lastPage; i++) pages.push(i);
      return pages;
    }

    pages.push(1);

    if (pageSafe > 4) {
      pages.push("left-ellipsis");
    } else {
      for (let i = 2; i <= 3; i++) pages.push(i);
    }

    const start = Math.max(pageSafe - 1, 4);
    const end = Math.min(pageSafe + 1, lastPage - 3);

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (pageSafe < lastPage - 3) {
      pages.push("right-ellipsis");
    } else {
      for (let i = lastPage - 2; i < lastPage; i++) pages.push(i);
    }

    pages.push(lastPage);

    return pages;
  }, [lastPage, pageSafe]);

  /* ── assign staff ─────────────────────────────────────────── */
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignGroup, setAssignGroup] = useState(null);
  const [assignAnchorEl, setAssignAnchorEl] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [saving, setSaving] = useState(false);

  const openAssign = useCallback((g, anchorEl) => {
    if (!isHead) return;

    setAssignGroup(g);
    setSelectedIds(getResponsibles(g).map((u) => respId(u)).filter(Boolean));
    setAssignAnchorEl(anchorEl || null);
    setAssignOpen(true);
  }, [isHead]);

  const closeAssign = useCallback(() => {
    setAssignOpen(false);
    setAssignGroup(null);
    setAssignAnchorEl(null);
    setSelectedIds([]);
    setSaving(false);
  }, []);

  const saveAssign = useCallback(async () => {
    if (!assignGroup) return;

    setSaving(true);

    try {
      const body = selectedIds.length <= 1
        ? { user_id: selectedIds[0] ?? null }
        : { user_ids: selectedIds };

      await axios.post(`/backoffice/groups/${assignGroup.id}/assign-responsibles`, body, { headers });

      closeAssign();
      loadGroups({ soft: true });
    } catch (e) {
      alert(e?.response?.data?.message || e?.message || t.err);
      setSaving(false);
    }
  }, [assignGroup, selectedIds, headers, closeAssign, loadGroups, t.err]);

  const removeResponsible = useCallback(async (group, userId) => {
    if (group?.id == null || userId == null) return;

    try {
      await axios.post(`/backoffice/groups/${group.id}/responsibles-remove`, { user_id: userId }, { headers });
      loadGroups({ soft: true });
    } catch (e) {
      alert(e?.response?.data?.message || e?.message || t.err);
    }
  }, [headers, loadGroups, t.err]);

  /* ── profile ──────────────────────────────────────────────── */
  const loadProfileReviews = useCallback(async (user, pageNum = 1) => {
    const userId = user?.user_id ?? user?.id;
    if (!userId) return;

    setProfileReviewsLoading(true);

    try {
      const res = await axios.get(`/backoffice/users/${userId}/reviews`, {
        headers,
        params: { page: pageNum },
      });

      const payload = res?.data?.data || res?.data || null;

      setProfileReviewsResp(payload);
      setProfileMyReview(payload?.my_review || null);
    } catch {
      setProfileReviewsResp(null);
      setProfileMyReview(null);
    } finally {
      setProfileReviewsLoading(false);
    }
  }, [headers]);

  const openProfile = useCallback(async (user) => {
    if (!user) return;

    setProfileUser(user);
    setProfileOpen(true);
    setProfileDetails(null);
    setProfileReviewsResp(null);
    setProfileMyReview(null);
    setProfileLoading(true);
    setProfileReviewsLoading(true);

    const userId = user?.user_id ?? user?.id;

    if (!userId) {
      setProfileLoading(false);
      setProfileReviewsLoading(false);
      return;
    }

    const [userRes, reviewsRes] = await Promise.allSettled([
      axios.get(`/backoffice/users/${userId}`, { headers }),
      axios.get(`/backoffice/users/${userId}/reviews`, { headers }),
    ]);

    if (userRes.status === "fulfilled") {
      setProfileDetails(userRes.value?.data?.data || userRes.value?.data || null);
    } else {
      setProfileDetails(null);
    }

    if (reviewsRes.status === "fulfilled") {
      const payload = reviewsRes.value?.data?.data || reviewsRes.value?.data || null;
      setProfileReviewsResp(payload);
      setProfileMyReview(payload?.my_review || null);
    } else {
      setProfileReviewsResp(null);
      setProfileMyReview(null);
    }

    setProfileLoading(false);
    setProfileReviewsLoading(false);
  }, [headers]);

  const closeProfile = useCallback(() => {
    setProfileOpen(false);
    setProfileUser(null);
    setProfileDetails(null);
    setProfileLoading(false);
    setProfileReviewsLoading(false);
    setProfileReviewsResp(null);
    setProfileMyReview(null);
  }, []);

  /* ── app view modal ───────────────────────────────────────── */
  const [appViewOpen, setAppViewOpen] = useState(false);
  const [appViewItem, setAppViewItem] = useState(null);

  const openAppView = useCallback((appItem) => {
    setAppViewItem(appItem);
    setAppViewOpen(true);
  }, []);

  const closeAppView = useCallback(() => {
    setAppViewOpen(false);
    setAppViewItem(null);
  }, []);

  /* ── loading skeleton ─────────────────────────────────────── */
  if (loading) {
    return (
      <section className="groups-list-page">
        <h2>{t.title}</h2>

        <ul className="gl-list">
          {Array.from({ length: 4 }).map((_, i) => (
            <li className="gl-item" key={i}>
              <div className="gl-summary gl-skel">
                <div className="gl-skel__title" />
                <div className="gl-skel__meta" />
              </div>
            </li>
          ))}
        </ul>
      </section>
    );
  }

  return (
    <section className="groups-list-page">
      <h2>{t.title}</h2>

      {/* ── Filters ─────────────────────────────────────────── */}
      <div className="gl-filters">
        <input
          className="gl-input"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          placeholder={t.searchPh}
        />

        <label className="gl-checkbox">
          <input
            type="checkbox"
            checked={onlyNotAssigned}
            onChange={(e) => {
              setOnlyNotAssigned(e.target.checked);
              setPage(1);
            }}
          />
          <span>{t.onlyNotAssigned}</span>
        </label>

        <label className="gl-select">
          <span>{t.sortLabel}</span>

          <select
            value={`${sortBy}:${sortDir}`}
            onChange={(e) => {
              const [sb, sd] = e.target.value.split(":");
              setSortBy(sb);
              setSortDir(sd);
              setPage(1);
            }}
          >
            <option value="sent_at:desc">{t.sortSentDesc}</option>
            <option value="sent_at:asc">{t.sortSentAsc}</option>
            <option value="title:asc">{t.sortTitleAsc}</option>
            <option value="title:desc">{t.sortTitleDesc}</option>
          </select>
        </label>

        <label className="gl-select">
          <span>{t.perPage}</span>

          <select
            value={perPage}
            onChange={(e) => {
              setPerPage(Number(e.target.value) || 10);
              setPage(1);
            }}
          >
            {[10, 20, 30, 50].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>

        <button className="gl-btn gl-btn--ghost" onClick={resetFilters}>
          {t.reset}
        </button>

        <button
          className="gl-btn"
          onClick={() => loadGroups({ soft: true })}
          disabled={refreshing}
        >
          {refreshing ? t.loading : t.refresh}
        </button>
      </div>

      {err && <div className="gl-error">{err}</div>}

      {/* ── Group list ──────────────────────────────────────── */}
      <ul className="gl-list">
        {!refreshing && paged.length === 0 && (
          <li className="gl-item">
            <div className="gl-summary-wrap">
              <div className="gl-summary gl-summary--main">
                <div className="gl-head">
                  <div className="gl-title">{t.empty}</div>
                </div>
              </div>
            </div>
          </li>
        )}

        {paged.map((g) => {
          const isOpen = expandedIds.has(String(g.id));
          const responsibles = getResponsibles(g);
          const apps = Array.isArray(g.applications) ? g.applications : [];

          const groupStatusRaw = pick(g, "status", "-");
          const regNumber = getRegistrationNumber(g);
          const groupSentAt = getSentAt(g);
          const groupDate = groupSentAt ? dateOnly(groupSentAt) : "—";

          const newAppsCount = apps.filter((a) => isAppNeedsDecision(a)).length;

          const pubName =
            g?.publisher_name ||
            g?.publisher?.organization_name ||
            g?.publisher?.full_name ||
            g?.publisher?.name ||
            g?.publisherName ||
            "—";

          return (
            <li key={g.id} className={`gl-item ${isOpen ? "is-open" : ""}`}>
              <div className="gl-summary-wrap">
                <div
                  className="gl-summary gl-summary--main"
                  role="button"
                  tabIndex={0}
                  onClick={() => toggleExpand(g.id)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      toggleExpand(g.id);
                    }
                  }}
                  aria-expanded={isOpen}
                >
                  <div className="gl-head">
                    <div className="gl-title">{g.title || `#${g.id}`}</div>

                    <div className="gl-subtitle">
                      <span>{t.publisher}: {pubName}</span>

                      <span
                        className={`gl-pill gl-pill--code ${!regNumber ? "gl-pill--muted" : ""}`}
                        title={regNumber || t.noRegistrationNumber}
                      >
                        № {regNumber || "—"}
                      </span>

                      <span className="gl-pill gl-pill--date">
                        {groupDate}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="gl-summary-meta">
                  {newAppsCount > 0 && (
                    <div
                      className="gl-needs-decision"
                      style={{ animation: "badge-pulse 1.2s ease-in-out infinite" }}
                    >
                      <span className="gl-needs-decision__badge">{newAppsCount}</span>
                      <span className="gl-needs-decision__label">{t.needsDecision}</span>
                    </div>
                  )}

                  <span
                    className={groupHeaderClass(groupStatusRaw)}
                    title={String(groupStatusRaw || "-")}
                  >
                    {statusLabel(groupStatusRaw)}
                  </span>

                  {isHead && (
                    <ResponsibleAvatars
                      items={responsibles}
                      canManage={isHead}
                      onRemove={(userId) => removeResponsible(g, userId)}
                      onViewProfile={openProfile}
                      max={4}
                      showAdd={isHead}
                      addTitle={t.assign}
                      onAdd={(e) => openAssign(g, e.currentTarget)}
                      label={t.assignees}
                    />
                  )}

                  <button
                    type="button"
                    className="gl-chevron-btn"
                    aria-label={isOpen ? t.close : t.open}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleExpand(g.id);
                    }}
                  >
                    <span className="gl-chevron" aria-hidden="true" />
                  </button>
                </div>
              </div>

              {isOpen && (
                <div className="gl-details gl-details--scroll">
                  <div className="gl-ginfo">
                    <div className="gl-ginfo__head">
                      <div className="gl-ginfo__title">{t.groupInfoTitle}</div>
                    </div>

                    <div className="gl-ginfo__grid">
                      {[
                        [t.registration_number, regNumber || "—"],
                        [t.subject, localizedName(g?.subject, locale)],
                        [t.grade, localizedName(g?.grade, locale)],
                        [t.education_level, localizedName(g?.education_level || g?.educationLevel, locale)],
                        [t.direction, localizedName(g?.direction, locale)],
                        [t.development_language, localizedName(g?.development_language || g?.developmentLanguage, locale)],
                        [t.teaching_language, localizedName(g?.teaching_language || g?.teachingLanguage, locale)],
                        [t.publication_type, localizedName(g?.publication_type || g?.publicationType, locale)],
                        [t.service_type, localizedName(g?.service_type || g?.serviceType, locale)],
                        [t.submission_year, localizedName(g?.submission_year || g?.submissionYear, locale)],
                        [t.sent_at, groupDate],
                      ].map(([label, value]) => (
                        <div className="gl-ginfo__item" key={label}>
                          <div className="gl-ginfo__label">{label}</div>
                          <div className="gl-ginfo__value">{value}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="apps-group__body">
                    <div className="gl-ginfo__head" style={{ marginBottom: 8 }}>
                      <div className="gl-ginfo__title">
                        {t.apps}

                        {apps.length > 0 && (
                          <span className="gl-pill" style={{ marginLeft: 8 }}>
                            {apps.length}
                          </span>
                        )}
                      </div>
                    </div>

                    {apps.length ? (
                      <table className="apps-table">
                        <thead>
                          <tr>
                            <th>{t.appCategory}</th>
                            <th>{t.appStatus}</th>
                            <th>{t.appDecision}</th>
                            <th>{t.registration_number}</th>
                            <th>{t.appDate}</th>
                            <th />
                          </tr>
                        </thead>

                        <tbody>
                          {apps.map((a) => {
                            const appSentAt = getSentAt(a) || groupSentAt;
                            const appRegNumber = getRegistrationNumber(a) || regNumber;
                            const appNeedsDecision = isAppNeedsDecision(a);

                            return (
                              <tr key={a.id}>
                                <td data-label={t.appCategory}>
                                  {localizedName(a?.category, locale)}
                                </td>

                                <td data-label={t.appStatus}>
                                  <span
                                    className={statusBadgeClass(a.status)}
                                    style={statusBadgeStyle(a)}
                                    title={String(a.status || "-")}
                                  >
                                    {statusLabel(a.status)}
                                  </span>

                                  {appNeedsDecision && (
                                    <span
                                      className="gl-pill gl-pill--muted"
                                      style={{ marginLeft: 6 }}
                                    >
                                      {t.needsDecision}
                                    </span>
                                  )}
                                </td>

                                <td data-label={t.appDecision}>
                                  {formatDecision(a.decision)}
                                </td>

                                <td data-label={t.registration_number}>
                                  {appRegNumber || "—"}
                                </td>

                                <td data-label={t.appDate}>
                                  {dateOnly(appSentAt)}
                                </td>

                                <td data-label="">
                                  <div className="apps-actions">
                                    <button
                                      type="button"
                                      className="btn btn--view btn--icon"
                                      onClick={(e) => {
                                        e.stopPropagation();

                                        openAppView({
                                          ...a,

                                          registration_number: appRegNumber,
                                          sent_at: appSentAt,

                                          group: {
                                            ...(a?.group || {}),
                                            id: g.id,
                                            title: g.title,
                                            registration_number: regNumber,
                                            sent_at: groupSentAt,

                                            subject: g.subject,
                                            grade: g.grade,
                                            education_level: g.education_level || g.educationLevel,
                                            direction: g.direction,
                                            development_language: g.development_language || g.developmentLanguage,
                                            teaching_language: g.teaching_language || g.teachingLanguage,
                                            publication_type: g.publication_type || g.publicationType,
                                            service_type: g.service_type || g.serviceType,
                                            submission_year: g.submission_year || g.submissionYear,
                                          },
                                        });
                                      }}
                                    >
                                      <svg
                                        width="14"
                                        height="14"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      >
                                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                        <circle cx="12" cy="12" r="3" />
                                      </svg>

                                      {t.viewFull}
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    ) : (
                      <div className="gl-warning">{t.empty}</div>
                    )}
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>

      <div className="gl-pagination">
        <button
          className="gl-page-btn"
          disabled={!canPrev}
          onClick={() => gotoPage(pageSafe - 1)}
        >
          ← {t.prev}
        </button>

        <div className="gl-page-numbers">
          {getPageButtons().map((p, idx) =>
            p === "left-ellipsis" || p === "right-ellipsis" ? (
              <span key={idx} className="gl-ellipsis">…</span>
            ) : (
              <button
                key={p}
                className={`gl-page-btn ${p === pageSafe ? "is-active" : ""}`}
                onClick={() => gotoPage(p)}
                disabled={p === pageSafe}
              >
                {p}
              </button>
            )
          )}
        </div>

        <button
          className="gl-page-btn"
          disabled={!canNext}
          onClick={() => gotoPage(pageSafe + 1)}
        >
          {t.next} →
        </button>

        <div className="gl-page-info">
          {t.page} {pageSafe} {t.of} {lastPage} • {total}
        </div>
      </div>

      <AssignStaffPopover
        open={assignOpen}
        anchorEl={assignAnchorEl}
        title={t.modalTitle}
        subtitle={assignGroup?.title || (assignGroup ? `#${assignGroup.id}` : "")}
        headers={headers}
        staffEndpoint="/backoffice/users"
        staffParams={staffParams}
        value={selectedIds}
        onChange={setSelectedIds}
        onClose={closeAssign}
        onSave={saveAssign}
        saving={saving}
        loadingText={t.loading}
        emptyText={t.staffEmpty}
        cancelText={t.cancel}
        saveText={t.save}
      />

      <ApplicationFullViewModal
        open={appViewOpen}
        onClose={closeAppView}
        onChanged={() => loadGroups({ soft: true })}
        headers={headers}
        t={t}
        app={appViewItem}
        endpoint="/backoffice/applications"
        locale={langHeader}
        embedded={true}
      />

      {profileOpen && (
        <div
          className="ga-modal-backdrop"
          role="dialog"
          aria-modal="true"
          onClick={closeProfile}
        >
          <div
            className="ga-modal-card ga-modal-card--xl"
            onClick={(e) => e.stopPropagation()}
            aria-label={t.profileTitle}
          >
            <div className="ga-modal-head">
              <h3 className="ga-modal-title">{t.profileTitle}</h3>

              <button
                type="button"
                className="ga-modal-close"
                onClick={closeProfile}
                aria-label={t.closeProfile}
              >
                ×
              </button>
            </div>

            <div className="ga-modal-body">
              {profileLoading ? (
                <div className="avh-state">
                  <span className="avh-spinner avh-spinner--lg" />
                  <span>{t.loading}</span>
                </div>
              ) : (
                <UserProfileModal
                  user={profileUser}
                  userDetails={profileDetails}
                  locale={locale}
                  reviewsLoading={profileReviewsLoading}
                  reviewsResp={profileReviewsResp}
                  myReview={profileMyReview}
                  canWriteReview={true}
                  canManageReview={isHead}
                  onReviewsTabOpen={() => loadProfileReviews(profileUser, 1)}
                  onReviewsPageChange={(p) => loadProfileReviews(profileUser, p)}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}